import { Component } from '@theme/component';
import { wishlist } from '@theme/wishlist-store';

/** Loads server-rendered wishlist cards for saved product IDs. */
class WishlistPageComponent extends Component {
  requiredRefs = ['empty', 'filled', 'list', 'loading', 'error'];

  connectedCallback() {
    super.connectedCallback();

    const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    const isDemo = new URLSearchParams(window.location.search).get('wishlist_demo') === '1';
    const demoProductIds = ['9569126514943', '9569123565823', '9569124188415'];

    this.isDemo = isLocal && isDemo;
    if (this.isDemo) { this.loadProducts(demoProductIds); return; }
    this.unsubscribe?.();
    this.unsubscribe = wishlist.subscribe((state) => {
      if (state.status === 'error') {
        this.refs.loading.hidden = true;
        this.refs.error.hidden = false;
        return;
      }
      if (state.status !== 'ready') return;
      const ids = [...new Set(state.items.map((item) => item.productId))];
      const signature = JSON.stringify(state.items.map(({ productId, variantId }) => [productId, variantId]));
      if (signature === this.signature) return;
      this.signature = signature;
      this.loadProducts(ids);
    });
    wishlist.ready().catch(() => {});
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    this.renderVersion = (this.renderVersion || 0) + 1;
    this.signature = undefined;
    super.disconnectedCallback();
  }

  async removeItem(event) {
    if (!this.isDemo) {
      const card = event.target.closest('wishlist-item-card');
      if (!card) return;
      try { await wishlist.remove({ productId: card.dataset.productId }); }
      catch { this.refs.error.hidden = false; }
      return;
    }
    event.target.closest('wishlist-item-card')?.remove();
    if (!this.refs.list.querySelector('wishlist-item-card')) {
      this.refs.filled.hidden = true;
      this.refs.empty.hidden = false;
    }
  }

  async loadProducts(ids) {
    const version = this.renderVersion = (this.renderVersion || 0) + 1;
    this.refs.empty.hidden = true;
    this.refs.filled.hidden = true;
    this.refs.error.hidden = true;
    this.refs.loading.hidden = false;
    this.setAttribute('aria-busy', 'true');
    if (!ids.length) {
      this.refs.loading.hidden = true;
      this.refs.empty.hidden = false;
      this.removeAttribute('aria-busy');
      return;
    }

    const url = new URL(this.dataset.searchUrl, window.location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', 'wishlist-cards');

    try {
      const cards = [];
      for (let index = 0; index < ids.length; index += 50) {
        url.searchParams.set('q', ids.slice(index, index + 50).map((id) => `id:${id}`).join(' OR '));
        const response = await fetch(url, { signal: AbortSignal.timeout(15000) });
        if (!response.ok) throw new Error(`Wishlist lookup failed (${response.status})`);
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        cards.push(...parsed.querySelectorAll('wishlist-item-card[data-product-id]'));
      }
      // Restore the saved variant using Liquid, not client-side card updates.
      if (!this.isDemo) {
        const savedItems = wishlist.getState().items;
        for (let index = 0; index < cards.length; index += 1) {
          const card = cards[index];
          const saved = savedItems.find((item) => item.productId === card.dataset.productId);
          if (!saved || saved.variantId === card.dataset.selectedVariantId) continue;
          const cardUrl = new URL(card.dataset.productUrl, window.location.origin);
          cardUrl.searchParams.set('variant', saved.variantId);
          cardUrl.searchParams.set('section_id', 'wishlist-card');
          const response = await fetch(cardUrl, { signal: AbortSignal.timeout(15000) });
          if (!response.ok) throw new Error('Could not render saved variant');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const replacement = parsed.querySelector('wishlist-item-card');
          if (replacement?.dataset.productId !== saved.productId) throw new Error('Invalid saved card');
          cards[index] = replacement;
        }
      }
      const orderedCards = ids
        .map((id) => cards.find((card) => card.dataset.productId === id))
        .filter(Boolean)
        .map((card) => {
          const imported = document.importNode(card, true);
          imported.querySelectorAll('script').forEach((script) => script.remove());
          return imported;
        });

      if (!this.isConnected || version !== this.renderVersion) return;
      this.refs.list.replaceChildren(...orderedCards);
      this.refs.empty.hidden = orderedCards.length > 0;
      this.refs.filled.hidden = orderedCards.length === 0;
    } catch (error) {
      console.error('Could not load demo wishlist products', error);
      if (this.isConnected && version === this.renderVersion) { this.refs.error.hidden = false; this.signature = undefined; }
    } finally {
      if (this.isConnected && version === this.renderVersion) {
        this.refs.loading.hidden = true;
        this.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('wishlist-page-component')) {
  customElements.define('wishlist-page-component', WishlistPageComponent);
}
